import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { vatPlaceholder } from '../../../../common/FunctionUtils';
import countries from 'countries-list';
import type { CustomerFormData, ValidationErrors } from '../types';
import { Tooltip } from '../../../components/common/tooltip.component';
import { validateVatNumber, validateCustomerName } from '../validations';
import { europeanVatCountryCodes } from '../../../../common/StringUtils';
import { RESTRICTED_COUNTRY_CODES } from '../../../utils/countries';

interface BusinessInformationProps {
    formData: CustomerFormData;
    errors: ValidationErrors;
    vatNumberError: string;
    onInputChange: (field: string, value: string) => void;
    onNumberOnlyInput: (e: React.FormEvent<HTMLInputElement>) => void;
    onSSNInput: (e: React.FormEvent<HTMLInputElement>, maxLength?: number) => void;
    onSSNKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onVatNumberInput: (e: React.FormEvent<HTMLInputElement>) => void;
    onVatNumberKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onZipCodeInput: (e: React.FormEvent<HTMLInputElement>) => void;
    setVatNumberError?: (error: string) => void;
    isVatValidating?: boolean;
    vatApiValidationStatus?: 'valid' | 'invalid' | 'error' | null;
    onVatBlur?: (vatNumber: string) => void;
    isVatAlreadyVerified?: boolean;
}

const BusinessInformation: React.FC<BusinessInformationProps> = ({
    formData,
    errors,
    vatNumberError,
    onInputChange,
    onNumberOnlyInput,
    onSSNInput,
    onSSNKeyDown,
    onVatNumberInput,
    onVatNumberKeyDown,
    onZipCodeInput,
    setVatNumberError,
    isVatValidating,
    vatApiValidationStatus,
    onVatBlur,
    isVatAlreadyVerified,
}) => {
    const { t } = useTranslation();

    const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
    const [countrySearchQuery, setCountrySearchQuery] = useState('');
    const countryDropdownRef = useRef<HTMLDivElement>(null);
    const countrySearchInputRef = useRef<HTMLInputElement>(null);

    const [isBusinessTypeDropdownOpen, setIsBusinessTypeDropdownOpen] = useState(false);
    const businessTypeDropdownRef = useRef<HTMLDivElement>(null);
    
    // Local error for the customer / business name when invalid characters are typed.
    const [customerNameError, setCustomerNameError] = useState('');

    const handleCustomerNameChange = (value: string) => {
        onInputChange('customerName', value);
        const trimmedValue = value.trim();
        if (trimmedValue && !validateCustomerName(trimmedValue)) {
            setCustomerNameError('CUSTOMER_NAME_INVALID_CHARACTERS');
        } else {
            setCustomerNameError('');
        }
    };

    const countryCodeOptions = Object.keys(countries.countries)
        .filter((code) => !RESTRICTED_COUNTRY_CODES.includes(code))
        .map((code) => ({
            value: code,
            label: code,
        }));

    // Generate country options for the country dropdown (same as EditCustomerDialog)
    const countryOptions = useMemo(() => {
        // Build the set of restricted country names from the same source, so the
        // names always match the dropdown labels regardless of package naming.
        const restrictedNames = new Set<string>();
        RESTRICTED_COUNTRY_CODES.forEach((code) => {
            const restricted = (countries.countries as any)[code];
            if (restricted?.name) {
                restrictedNames.add(restricted.name);
            }
        });

        const newDetails: any[] = [];
        Object.keys(countries.countries).forEach((code) => {
            newDetails.push({ ...(countries.countries as any)[code], code });
        });

        const allCountries = [
            { value: "Sweden", label: "Sweden", code: "SE" },
            { value: "USA", label: "USA", code: "US" },
            { value: "Canada", label: "Canada", code: "CA" },
            { value: "Panama", label: "Panama", code: "PA" },
            ...newDetails.map((country) => ({
                value: country.name,
                label: country.name,
                code: country.code,
            })),
        ];

        // Remove duplicates by creating a Map with country name as key
        const uniqueCountriesMap = new Map();
        allCountries.forEach(country => {
            if (!uniqueCountriesMap.has(country.value)) {
                uniqueCountriesMap.set(country.value, country);
            }
        });

        // Exclude restricted / sanctioned countries from the selectable list
        return Array.from(uniqueCountriesMap.values()).filter(
            (country) => !restrictedNames.has(country.value)
        );
    }, []);

    // Translate a country's display label via its code (e.g. COUNTRY_SE).
    // Falls back to the original English name when no translation exists,
    // so the stored value (formData.country) is never affected.
    const getCountryLabel = (option?: { code?: string; label?: string; value?: string }) => {
        if (!option) return '';
        if (option.code) {
            return t(`COUNTRY_${option.code}`, { defaultValue: option.label || option.value || '' });
        }
        return option.label || option.value || '';
    };

    // Memoize Sweden country
    const swedenCountry = useMemo(() => {
        return countryOptions.find(country => country.value === 'Sweden');
    }, [countryOptions]);

    // Memoize sorted countries (excluding Sweden) - sorted by translated label
    const sortedCountries = useMemo(() => {
        return countryOptions
            .filter(country => country.value !== 'Sweden')
            .sort((a, b) => getCountryLabel(a).localeCompare(getCountryLabel(b)));
    }, [countryOptions, t]);

    // Normalize for accurate, case- and accent-insensitive matching
    // (e.g. "sWedEn", "Sweden", "sverige", "Åland" all behave predictably).
    const normalizeForSearch = (str: string) =>
        (str || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(new RegExp('[\\u0300-\\u036f]', 'g'), '') // strip accent marks
            .trim();

    // A country matches when the query is the start of the whole name OR the start
    // of any word in it. This prevents false hits like "Ryssland" matching
    // "Vitryssland" (where it is only a mid-word substring), while still allowing
    // partial searches like "swe" -> Sweden or "arab" -> United Arab Emirates.
    const matchesQuery = (country: { label?: string; value?: string; code?: string }, query: string) => {
        return [country.label, getCountryLabel(country)].some((name) => {
            const normalized = normalizeForSearch(name || '');
            if (normalized.startsWith(query)) return true;
            return normalized.split(/[\s(),.\/-]+/).some((word) => word.startsWith(query));
        });
    };

    // Filter countries based on search query (matches both English name and translated label).
    // Names that START with the query are ranked first for better relevance.
    const filteredCountries = useMemo(() => {
        const query = normalizeForSearch(countrySearchQuery);
        if (!query) {
            return sortedCountries;
        }
        const matches = sortedCountries.filter(country => matchesQuery(country, query));
        return matches.sort((a, b) => {
            const aStarts =
                normalizeForSearch(a.label).startsWith(query) ||
                normalizeForSearch(getCountryLabel(a)).startsWith(query);
            const bStarts =
                normalizeForSearch(b.label).startsWith(query) ||
                normalizeForSearch(getCountryLabel(b)).startsWith(query);
            if (aStarts && !bStarts) return -1;
            if (!aStarts && bStarts) return 1;
            return getCountryLabel(a).localeCompare(getCountryLabel(b));
        });
    }, [sortedCountries, countrySearchQuery, t]);

    // Translated label of the currently selected country (falls back to raw value)
    const selectedCountryDisplay = (() => {
        const opt = countryOptions.find(c => c.value === formData.country);
        return opt ? getCountryLabel(opt) : (formData.country || '');
    })();

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
                setIsCountryDropdownOpen(false);
                setCountrySearchQuery('');
            }
            if (businessTypeDropdownRef.current && !businessTypeDropdownRef.current.contains(event.target as Node)) {
                setIsBusinessTypeDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isCountryDropdownOpen && countrySearchInputRef.current) {
            setTimeout(() => {
                countrySearchInputRef.current?.focus();
            }, 100);
        } else if (!isCountryDropdownOpen) {
            setCountrySearchQuery('');
        }
    }, [isCountryDropdownOpen]);

    // Auto-set VAT registration to 'Yes' and keep it disabled when business type is 2 or 3 and country is Sweden
    useEffect(() => {
        if ((formData.businessType === '2' || formData.businessType === '3') && formData.country === 'Sweden') {
            if (formData.isVATRegistered !== 'true') {
                onInputChange('isVATRegistered', 'true');
            }
        }
    }, [formData.businessType, formData.country]);

    // Extract country code from VAT number when data is loaded
    useEffect(() => {
        if (formData.vatNumber && formData.vatNumber.length > 2) {
           // Extract first 2 characters as potential country code
            const potentialCountryCode = formData.vatNumber.substring(0, 2).toUpperCase();
           // CRITICAL: Check if VAT starts with double country code (e.g., PRPR)
            const doubleCountryCode = potentialCountryCode + potentialCountryCode;
            if (formData.vatNumber.toUpperCase().startsWith(doubleCountryCode)) {
                // Remove one instance of the duplicate
                const fixedVatNumber = formData.vatNumber.substring(potentialCountryCode.length);
                onInputChange('vatNumber', fixedVatNumber);
                // Don't continue with country code detection, let it re-run after fix
                return;
            }
            
            // Check if this is a valid country code in our list
            const isValidCountryCode = countryCodeOptions.some(opt => opt.value === potentialCountryCode);
           // Only update if it's a valid country code and different from current
            if (isValidCountryCode && formData.vatCountryCode !== potentialCountryCode) {
               onInputChange('vatCountryCode', potentialCountryCode);
            }
        }
    }, [formData.vatNumber]);

    

    const getVatNumberWithoutCountryCode = (fullVatNumber: string, countryCode: string): string => {
        if (!fullVatNumber) return '';
        // First check if it starts with the provided country code
        if (countryCode && fullVatNumber.startsWith(countryCode)) {
            const stripped = fullVatNumber.slice(countryCode.length);
            return stripped;
        }
        
        // If not, check if it starts with any valid country code (for initial load)
        const firstTwoChars = fullVatNumber.substring(0, 2).toUpperCase();
        const isValidCountryCode = countryCodeOptions.some(opt => opt.value === firstTwoChars);
        
        if (isValidCountryCode) {
            const stripped = fullVatNumber.slice(2); 
            return stripped;
        }
        
        return fullVatNumber;
    };

    const businessTypeOptions = [
        { value: '1', label: t('PRIVATE_CUSTOMER') },
        { value: '2', label: t('BUSINESS_CUSTOMER') },
        { value: '3', label: t('BUSINESS_CONSTRUCTION') }
    ];

    const getBusinessTypeLabel = (value: string) => {
        const option = businessTypeOptions.find(opt => opt.value === value);
        return option ? option.label : '';
    };

    return (
        <>
            <Row className="mb-4">
                <Col md={6}>
                    <Form.Group className="input-field">
                        <Form.Label>{t('BUSINESS_TYPE')}</Form.Label>
                        <div className="custom-dropdown" ref={businessTypeDropdownRef} style={{ position: 'relative' }}>
                            <div
                                className={`dropdown-trigger ${isBusinessTypeDropdownOpen ? 'open' : ''}`}
                                onClick={() => setIsBusinessTypeDropdownOpen(!isBusinessTypeDropdownOpen)}
                                style={{
                                    position: 'relative',
                                    height: '46px',
                                    border: `1px solid ${errors.businessType ? '#dc3545' : (isBusinessTypeDropdownOpen ? '#7c3aed' : '#eaebf0')}`,
                                    borderRadius: '6px',
                                    backgroundColor: '#ffffff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '12px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    boxShadow: isBusinessTypeDropdownOpen ? '0 0 0 0.2rem rgba(168, 85, 247, 0.18)' : 'none'
                                }}
                            >
                                <span style={{
                                    flex: 1,
                                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                                    fontSize: '15px',
                                    fontWeight: 500,
                                    lineHeight: '22px',
                                    color: formData.businessType ? '#252525' : '#68727d'
                                }}>
                                    {formData.businessType ? getBusinessTypeLabel(formData.businessType) : t('SELECT')}
                                </span>
                                <svg
                                    className={`dropdown-arrow ${isBusinessTypeDropdownOpen ? 'rotated' : ''}`}
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    style={{
                                        flexShrink: 0,
                                        color: '#68727d',
                                        transition: 'transform 0.2s ease',
                                        transform: isBusinessTypeDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                                    }}
                                >
                                    <path
                                        d="M5 7.5L10 12.5L15 7.5"
                                        stroke="currentColor"
                                        strokeWidth="1.67"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </div>

                            {isBusinessTypeDropdownOpen && (
                                <div 
                                    className="dropdown-list"
                                    style={{
                                        position: 'absolute',
                                        top: 'calc(100% + 4px)',
                                        left: 0,
                                        width: '100%',
                                        backgroundColor: '#ffffff',
                                        border: '1px solid #eaebf0',
                                        borderRadius: '6px',
                                        padding: '4px',
                                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                                        zIndex: 1000
                                    }}
                                >
                                    <div className="dropdown-content">
                                        {businessTypeOptions.map((option) => (
                                            <div
                                                key={option.value}
                                                className={`list-item ${formData.businessType === option.value ? 'selected' : ''}`}
                                                onClick={() => {
                                                    onInputChange('businessType', option.value);
                                                    setIsBusinessTypeDropdownOpen(false);
                                                }}
                                                style={{
                                                    padding: '10px 12px',
                                                    cursor: 'pointer',
                                                    borderRadius: '4px',
                                                    transition: 'background-color 0.2s ease',
                                                    backgroundColor: formData.businessType === option.value ? '#f4efff' : 'transparent'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f4efff'}
                                                onMouseLeave={(e) => {
                                                    if (formData.businessType !== option.value) {
                                                        e.currentTarget.style.backgroundColor = 'transparent';
                                                    }
                                                }}
                                            >
                                                <div className="text-input">
                                                    <div className="paragraph-container">
                                                        <p className="option-text" style={{ margin: 0, fontSize: '15px', color: '#252525' }}>
                                                            {option.label}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        {errors.businessType && (
                            <Form.Text className="text-danger">{t(errors.businessType)}</Form.Text>
                        )}
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="input-field">
                        <Form.Label>
                            {t('COUNTRY')}
                            <Tooltip content={t('COUNTRY_SELECTION_INFO')} position="top">
                                <span className="info-icon ms-1 px-1" style={{color:"#7c3aed"}}>ⓘ</span>
                            </Tooltip>
                        </Form.Label>
                        <div className="custom-dropdown" ref={countryDropdownRef} style={{ position: 'relative' }}>
                            <div
                                className={`dropdown-trigger ${isCountryDropdownOpen ? 'open' : ''}`}
                                onClick={() => {
                                    if (!isCountryDropdownOpen) {
                                        setIsCountryDropdownOpen(true);
                                    }
                                }}
                                style={{
                                    position: 'relative',
                                    height: '46px',
                                    border: `1px solid ${errors.country ? '#dc3545' : (isCountryDropdownOpen ? '#7c3aed' : '#eaebf0')}`,
                                    borderRadius: '6px',
                                    backgroundColor: '#ffffff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '12px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    boxShadow: isCountryDropdownOpen ? '0 0 0 0.2rem rgba(168, 85, 247, 0.18)' : 'none'
                                }}
                            >
                                <input
                                    ref={countrySearchInputRef}
                                    type="text"
                                    className="dropdown-input"
                                    autoComplete="off"
                                    placeholder={t('SELECT')}
                                    value={isCountryDropdownOpen ? countrySearchQuery : selectedCountryDisplay}
                                    onChange={(e) => {
                                        setCountrySearchQuery(e.target.value);
                                        if (!isCountryDropdownOpen) {
                                            setIsCountryDropdownOpen(true);
                                        }
                                    }}
                                    onFocus={() => {
                                        if (!isCountryDropdownOpen) {
                                            setIsCountryDropdownOpen(true);
                                        }
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    readOnly={!isCountryDropdownOpen}
                                    style={{
                                        flex: 1,
                                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                                        fontSize: '15px',
                                        fontWeight: 500,
                                        lineHeight: '22px',
                                        color: formData.country ? '#252525' : '#68727d',
                                        border: 'none',
                                        outline: 'none',
                                        background: 'transparent',
                                        cursor: isCountryDropdownOpen ? 'text' : 'pointer',
                                        width: '100%'
                                    }}
                                />
                                <svg
                                    className={`dropdown-arrow ${isCountryDropdownOpen ? 'rotated' : ''}`}
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsCountryDropdownOpen(!isCountryDropdownOpen);
                                    }}
                                    style={{
                                        flexShrink: 0,
                                        color: '#68727d',
                                        transition: 'transform 0.2s ease',
                                        transform: isCountryDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <path
                                        d="M5 7.5L10 12.5L15 7.5"
                                        stroke="currentColor"
                                        strokeWidth="1.67"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </div>

                            {isCountryDropdownOpen && (
                                <div
                                    className="dropdown-list"
                                    style={{
                                        position: 'absolute',
                                        top: 'calc(100% + 4px)',
                                        left: 0,
                                        width: '100%',
                                        maxHeight: '250px',
                                        overflowY: 'auto',
                                        backgroundColor: '#ffffff',
                                        border: '1px solid #eaebf0',
                                        borderRadius: '6px',
                                        padding: '4px',
                                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                                        zIndex: 1000
                                    }}
                                >
                                    <div className="dropdown-content">
                                        {/* Sweden first - only show if matches search or no search */}
                                        {swedenCountry && (!normalizeForSearch(countrySearchQuery) ||
                                            matchesQuery(swedenCountry, normalizeForSearch(countrySearchQuery))
                                        ) && (
                                            <div
                                                key={swedenCountry.value}
                                                className={`list-item ${formData.country === swedenCountry.value ? 'selected' : ''}`}
                                                onClick={() => {
                                                    // Switching TO Sweden from another country: a non-Swedish SSN
                                                    // may be longer than / not match the 12-digit personnummer
                                                    // rule, so clear it for a fresh, valid entry.
                                                    if (formData.country !== swedenCountry.value) {
                                                        onInputChange('ssn', '');
                                                    }
                                                    onInputChange('country', swedenCountry.value);
                                                    setIsCountryDropdownOpen(false);
                                                    setCountrySearchQuery('');
                                                }}
                                                style={{
                                                    padding: '10px 12px',
                                                    cursor: 'pointer',
                                                    borderRadius: '4px',
                                                    transition: 'background-color 0.2s ease',
                                                    backgroundColor: formData.country === swedenCountry.value ? '#f4efff' : 'transparent'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f4efff'}
                                                onMouseLeave={(e) => {
                                                    if (formData.country !== swedenCountry.value) {
                                                        e.currentTarget.style.backgroundColor = 'transparent';
                                                    }
                                                }}
                                            >
                                                <div className="text-input">
                                                    <div className="paragraph-container">
                                                        <p className="option-text" style={{ margin: 0, fontSize: '15px', color: '#252525' }}>
                                                            {getCountryLabel(swedenCountry)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {/* All other countries sorted alphabetically and filtered */}
                                        {filteredCountries.length > 0 ? (
                                            filteredCountries.map((country) => (
                                                <div
                                                    key={country.value}
                                                    className={`list-item ${formData.country === country.value ? 'selected' : ''}`}
                                                    onClick={() => {
                                                        onInputChange('country', country.value);
                                                        setIsCountryDropdownOpen(false);
                                                        setCountrySearchQuery('');
                                                    }}
                                                    style={{
                                                        padding: '10px 12px',
                                                        cursor: 'pointer',
                                                        borderRadius: '4px',
                                                        transition: 'background-color 0.2s ease',
                                                        backgroundColor: formData.country === country.value ? '#f4efff' : 'transparent'
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f4efff'}
                                                    onMouseLeave={(e) => {
                                                        if (formData.country !== country.value) {
                                                            e.currentTarget.style.backgroundColor = 'transparent';
                                                        }
                                                    }}
                                                >
                                                    <div className="text-input">
                                                        <div className="paragraph-container">
                                                            <p className="option-text" style={{ margin: 0, fontSize: '15px', color: '#252525' }}>
                                                                {getCountryLabel(country)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div
                                                className="list-item no-results"
                                                style={{
                                                    padding: '16px 12px',
                                                    color: '#68727d',
                                                    textAlign: 'center',
                                                    cursor: 'default'
                                                }}
                                            >
                                                <div className="text-input">
                                                    <div className="paragraph-container">
                                                        <p className="option-text" style={{ margin: 0, fontSize: '15px' }}>
                                                            {t('NO_RESULTS_FOUND') || 'No countries found'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        {errors.country && (
                            <Form.Text className="text-danger">{t(errors.country)}</Form.Text>
                        )}
                    </Form.Group>
                </Col>
            </Row>

            <Row className="mb-4">
                {formData.businessType != '1' && (
                    <Col md={6}>
                        <Form.Group className="input-field">
                            <Form.Label>{t('DOES_CUSTOMER_HAVE_VAT_REGISTRATION')}</Form.Label>
                            <Form.Select
                                className="custom-select"
                                value={formData.isVATRegistered === 'true' ? 'true' : 'false'}
                                onChange={(e) => onInputChange('isVATRegistered', e.target.value)}
                                disabled={(formData.businessType === '3') && formData.country === 'Sweden'}
                            >
                                <option value="true">{t('YES')}</option>
                                <option value="false">{t('NO')}</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                )}
                {formData.businessType != '1' && formData.isVATRegistered === "true" && (
                    <Col md={6}>
                        <Form.Group className="input-field">
                            <Form.Label>
                                {t('VAT_NUMBER')}
                                <Form.Text className="text-muted d-inline ms-1"> {t('VAT_COUNTRY_CODE_AUTO_ADD')}</Form.Text>
                            </Form.Label>
                            <div className="vat-input-container" style={{ display: 'flex', alignItems: 'flex-start' }}>
                                <div className="vat-country-dropdown">
                                    <Form.Select
                                        className="vat-country-select"
                                        value={formData.vatCountryCode}
                                        onChange={(e) => {
                                            const newCountryCode = e.target.value;

                                            onInputChange('vatCountryCode', newCountryCode);

                                            // Update VAT number with new country code
                                            const currentVatWithoutCode = getVatNumberWithoutCountryCode(formData.vatNumber, formData.vatCountryCode);

                                            if (currentVatWithoutCode) {
                                                const newFullVat = newCountryCode + currentVatWithoutCode;

                                                // CRITICAL: Verify no double country code
                                                const doubleCode = newCountryCode + newCountryCode;
                                                if (newFullVat.startsWith(doubleCode)) {
                                                    const fixedVat = newFullVat.substring(newCountryCode.length);
                                                    onInputChange('vatNumber', fixedVat);

                                                    // Validate with new country code
                                                    if (setVatNumberError) {
                                                        validateVatNumber(newCountryCode, fixedVat, setVatNumberError, t);
                                                    }
                                                } else {
                                                    onInputChange('vatNumber', newFullVat);

                                                    // Validate with new country code
                                                    if (setVatNumberError) {
                                                        validateVatNumber(newCountryCode, newFullVat, setVatNumberError, t);
                                                    }
                                                }
                                            }
                                        }}
                                        style={{ width: 80, display: "inline-block", position: "relative", top: "0px" }}
                                    >
                                        {countryCodeOptions.map((opt) => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </div>
                                <div className="vat-number-input" style={{ flex: 1  , gap:0}}>
                                    <Form.Control
                                        type="text"
                                        className={errors.vatNumber ? 'is-invalid' : ''}
                                        placeholder={vatPlaceholder(formData.vatCountryCode)}
                                        value={getVatNumberWithoutCountryCode(formData.vatNumber, formData.vatCountryCode)}
                                        onChange={(e) => {
                                            const inputValue = e.target.value.trim();

                                            if (!inputValue) {
                                               onInputChange('vatNumber', '');
                                               if (setVatNumberError) setVatNumberError('');
                                                return;
                                            }

                                            // Build the complete VAT number
                                            let fullVatNumber = '';

                                            // Check if the input value already starts with the selected country code
                                            if (inputValue.toUpperCase().startsWith(formData.vatCountryCode.toUpperCase())) {
                                                fullVatNumber = inputValue.toUpperCase();
                                            } else {
                                                 fullVatNumber = formData.vatCountryCode + inputValue;
                                            }

                                            // CRITICAL: Check if we're about to create a double country code (e.g., PRPR)
                                            const doubleCountryCode = formData.vatCountryCode + formData.vatCountryCode;
                                            if (fullVatNumber.toUpperCase().startsWith(doubleCountryCode)) {
                                                fullVatNumber = fullVatNumber.substring(formData.vatCountryCode.length);
                                            }

                                            // Prevent double-addition: only update if the value actually changed
                                            if (fullVatNumber !== formData.vatNumber) {
                                               onInputChange('vatNumber', fullVatNumber);
                                               if (setVatNumberError) {
                                                   validateVatNumber(formData.vatCountryCode, fullVatNumber, setVatNumberError, t);
                                               }
                                            }
                                        }}
                                        onInput={onVatNumberInput}
                                        onKeyDown={onVatNumberKeyDown}
                                    />
                                </div>
                                {europeanVatCountryCodes.has(formData.vatCountryCode) && (
                                    <button
                                        type="button"
                                        disabled={!formData.vatNumber?.trim() || isVatValidating || isVatAlreadyVerified}
                                        onClick={() => {
                                            if (formData.vatNumber?.trim()) {
                                                onVatBlur?.(formData.vatNumber.trim());
                                            }
                                        }}
                                        style={{
                                            flexShrink: 0,
                                            height: '46px',
                                            padding: '0 14px',
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                                            color: '#ffffff',
                                            background: '#7c3aed',
                                            border: `1px solid #7c3aed`,
                                            borderRadius: '0px',
                                            cursor: (!formData.vatNumber?.trim() || isVatValidating || isVatAlreadyVerified) ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {isVatValidating ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                                {t('VERIFYING')}
                                            </>
                                        ) : (
                                            t('VERIFY')
                                        )}
                                    </button>
                                )}
                            </div>
                            {errors.vatNumber && (
                                <Form.Text className="text-danger">{t(errors.vatNumber)}</Form.Text>
                            )}
                            {vatNumberError && (
                                <Form.Text className="text-danger">{t(vatNumberError)}</Form.Text>
                            )}
                            {europeanVatCountryCodes.has(formData.vatCountryCode) && !isVatValidating && vatApiValidationStatus === 'valid' && (
                                <Form.Text className="text-success d-flex align-items-center gap-1 mt-1">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                                        <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                                    </svg>
                                    {t('VAT_VALID')}
                                </Form.Text>
                            )}
                            {europeanVatCountryCodes.has(formData.vatCountryCode) && !isVatValidating && vatApiValidationStatus === 'invalid' && (
                                <Form.Text className="text-danger d-flex align-items-center gap-1 mt-1">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                                        <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/>
                                    </svg>
                                    {t('VAT_INVALID')}
                                </Form.Text>
                            )}
                            {europeanVatCountryCodes.has(formData.vatCountryCode) && !isVatValidating && vatApiValidationStatus === 'error' && (
                                <Form.Text className="text-warning d-flex align-items-center gap-1 mt-1">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                                        <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                                    </svg>
                                    {t('VAT_VERIFICATION_FAILED')}
                                </Form.Text>
                            )}
                        </Form.Group>
                    </Col>
                )}
            </Row>

            <Row className="mb-4">
                <Col md={6}>
                    {formData.businessType === '1' ? (
                        <Form.Group className="input-field">
                            <Form.Label>{t('YOUR_12_DIGIT_SSN_NO')}</Form.Label>
                            <Form.Control
                                type="text"
                                className={errors.ssn ? 'is-invalid' : ''}
                                placeholder={t('YOUR_12_DIGIT_SSN_NO')}
                                value={formData.ssn}
                                onChange={(e) => onInputChange('ssn', e.target.value)}
                                // Cap at 12 digits only for Sweden; other countries have no length limit.
                                onInput={(e) => onSSNInput(e as React.FormEvent<HTMLInputElement>, (formData.country || '').trim().toLowerCase() === 'sweden' ? 12 : 0)}
                                onKeyDown={onSSNKeyDown}
                            />
                            {errors.ssn && (
                                <Form.Text className="text-danger">{t(errors.ssn)}</Form.Text>
                            )}
                        </Form.Group>
                    ) : (
                        <Form.Group className="input-field">
                            <Form.Label>
                                {t('ORGANIZATION_NUMBER')}
                                {/* <Form.Text className="text-muted d-inline ms-1"> {t('ONLY_NUMBERS_ALLOWED')}</Form.Text> */}
                            </Form.Label>
                            <Form.Control
                                type="text"
                                className={errors.orgNo ? 'is-invalid' : ''}
                                placeholder={t('ORGANIZATION_NUMBER')}
                                value={formData.orgNo}
                                onChange={(e) => onInputChange('orgNo', e.target.value)}
                            />
                            {errors.orgNo && (
                                <Form.Text className="text-danger">{t(errors.orgNo)}</Form.Text>
                            )}
                        </Form.Group>
                    )}
                </Col>
                <Col md={6}>
                    <Form.Group className="input-field">
                        <Form.Label>{t('CUSTOMER_FULL_NAME_BUSINESS_NAME')}</Form.Label>
                        <Form.Control
                            type="text"
                           className={(errors.customerName || customerNameError) ? 'is-invalid' : ''}
                            placeholder={t('TYPE_HERE')}
                            value={formData.customerName}
                            onChange={(e) => handleCustomerNameChange(e.target.value)}
                        />
                      {(errors.customerName || customerNameError) && (
                            <Form.Text className="text-danger">{t(errors.customerName || customerNameError)}</Form.Text>
                        )}
                    </Form.Group>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col md={6}>
                    <Form.Group className="input-field">
                        <Form.Label>{t('CITY')}</Form.Label>
                        <Form.Control
                            type="text"
                            className={errors.city ? 'is-invalid' : ''}
                            placeholder={t('TYPE_HERE')}
                            value={formData.city}
                            onChange={(e) => onInputChange('city', e.target.value)}
                            onKeyDown={(e) => {
                                const allowedCharacters = /^[a-zA-ZÀ-ÿ0-9\s,.\-/']$/;
                                if (
                                    e.key !== 'Backspace' &&
                                    !allowedCharacters.test(e.key) &&
                                    !['ArrowLeft', 'ArrowRight', 'Tab', 'Delete', 'Control', 'v', 'Meta', 'c', 'x', 'a'].includes(e.key) &&
                                    !(e.ctrlKey || e.metaKey)
                                ) {
                                    e.preventDefault();
                                }
                            }}
                        />
                        {errors.city && (
                            <Form.Text className="text-danger">{t(errors.city)}</Form.Text>
                        )}
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="input-field">
                        <Form.Label>{t('ZIP_CODE')}</Form.Label>
                        <Form.Control
                            type="text"
                            className={errors.zipCode ? 'is-invalid' : ''}
                            placeholder={t('TYPE_HERE')}
                            value={formData.zipCode}
                            maxLength={10}
                            onChange={(e) => {
                                const value = e.target.value;
                                // Allow letters, numbers, spaces and hyphens
                                if (value === '' || /^[A-Za-z0-9\s-,]{0,10}$/.test(value)) {
                                    onInputChange('zipCode', value);
                                }
                            }}
                            onInput={onZipCodeInput}
                        />
                        {errors.zipCode && (
                            <Form.Text className="text-danger">{t(errors.zipCode)}</Form.Text>
                        )}
                    </Form.Group>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col md={6}>
                    <Form.Group className="input-field">
                        <Form.Label>{t('BUSINESS_ADDRESS_LINE_1')}</Form.Label>
                        <Form.Control
                            type="text"
                            className={errors.addressLine1 ? 'is-invalid' : ''}
                            placeholder={t('TYPE_HERE')}
                            value={formData.addressLine1}
                            onChange={(e) => onInputChange('addressLine1', e.target.value)}
                            onKeyDown={(e) => {
                                const allowedCharacters = /^[a-zA-ZÀ-ÿ0-9\s,.\-/']$/;
                                if (
                                    e.key !== 'Backspace' &&
                                    !allowedCharacters.test(e.key) &&
                                    !['ArrowLeft', 'ArrowRight', 'Tab', 'Delete', 'Control', 'v', 'Meta', 'c', 'x', 'a'].includes(e.key) &&
                                    !(e.ctrlKey || e.metaKey)
                                ) {
                                    e.preventDefault();
                                }
                            }}
                        />
                        {errors.addressLine1 && (
                            <Form.Text className="text-danger">{t(errors.addressLine1)}</Form.Text>
                        )}
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="input-field">
                        <Form.Label>{t('BUSINESS_ADDRESS_LINE_2')}</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder={t('TYPE_HERE')}
                            value={formData.addressLine2}
                            onChange={(e) => onInputChange('addressLine2', e.target.value)}
                            onKeyDown={(e) => {
                                const allowedCharacters = /^[a-zA-ZÀ-ÿ0-9\s,.\-/']$/;
                                if (
                                    e.key !== 'Backspace' &&
                                    !allowedCharacters.test(e.key) &&
                                    !['ArrowLeft', 'ArrowRight', 'Tab', 'Delete', 'Control', 'v', 'Meta', 'c', 'x', 'a'].includes(e.key) &&
                                    !(e.ctrlKey || e.metaKey)
                                ) {
                                    e.preventDefault();
                                }
                            }}
                        />
                    </Form.Group>
                </Col>
            </Row>

        </>
    );
};

export default BusinessInformation;